import React from 'react'
import { getCampaignConfig, getHardcodedConfig } from './campaigns.config'
import type { CampaignStep as ConfigCampaignStep } from './campaigns.config'
import type { Campaign } from '@/features/campaigns'
import { gamingRegistry } from '../../campaigns/gaming-addiction/registry'
import { robloxRegistry } from '../../campaigns/roblox-exploitation/registry'
import { pfasRegistry } from '../../campaigns/pfas-drinking-water/registry'
import { acaRegistry } from '../../campaigns/aca-health-insurance/registry'
import type { CampaignRegistry, CampaignStageDefinition } from '../../campaigns/campaignRegistry.types'
import { validateStepByCampaign } from '../utils/validation/stepValidation'

const CAMPAIGN_REGISTRIES: CampaignRegistry[] = [
  gamingRegistry,
  robloxRegistry,
  pfasRegistry,
  acaRegistry,
]

const COMPONENT_REGISTRY: Record<string, React.ComponentType<any>> = CAMPAIGN_REGISTRIES.reduce(
  (registryMap, registry) => ({
    ...registryMap,
    ...registry.components,
  }),
  {} as Record<string, React.ComponentType<any>>
)

/**
 * Step configuration interface
 */
export interface CampaignStepConfig {
  id: string
  title: string
  description: string
  component: React.ComponentType<any>
  isRequired: boolean
  order: number
}

const normalizeIdentifier = (value?: string | null): string | null => {
  if (!value) return null
  return value.toString().toLowerCase().trim()
}

const resolveCampaignRegistry = (campaign: Campaign): CampaignRegistry | undefined => {
  const candidates = new Set<string>()

  const id = normalizeIdentifier(campaign.id)
  if (id) candidates.add(id)

  const name = normalizeIdentifier(campaign.name)
  if (name) candidates.add(name)

  // Some campaign records may expose a type/slug
  const type = normalizeIdentifier((campaign as any)?.type)
  if (type) candidates.add(type)

  if (candidates.size === 0) {
    return undefined
  }

  return CAMPAIGN_REGISTRIES.find(registry =>
    registry.identifiers
      .map(normalizeIdentifier)
      .filter((identifier): identifier is string => Boolean(identifier))
      .some(identifier => candidates.has(identifier))
  )
}

/**
 * Get campaign key for lookup
 * Uses campaign.id as it's guaranteed unique
 */
const getCampaignKey = (campaign: Campaign): string | null => {
  // Use campaign ID (guaranteed unique)
  if (campaign.id) {
    return campaign.id
  }

  console.error('❌ Campaign has no id:', campaign)
  return null
}

/**
 * Campaign Flow Factory
 * Generates step configurations from database-driven campaigns.config
 */
export class CampaignFlowFactory {
  /**
   * Create steps for a campaign based on its configuration
   */
  static createStepsForCampaign(
    campaign: Campaign,
    componentProps: any
  ): CampaignStepConfig[] {

    // ✅ Get campaign key (uses TYPE enum or database ID)
    const campaignKey = getCampaignKey(campaign)

    if (!campaignKey) {
      console.error('❌ Could not determine campaign key for:', campaign.name)
      return []
    }

    // ✅ Get campaign config from cache (loaded from database)
    let config = getCampaignConfig(campaignKey)
    const registry = resolveCampaignRegistry(campaign)

    // FALLBACK: If not found by ID, try to match by name/type for hardcoded campaigns
    if (!config) {
      console.warn(`⚠️ Config not found by ID (${campaignKey}), trying fallback by name: ${campaign.name}`)
      config = getHardcodedConfig(campaign.name) || getHardcodedConfig((campaign as any).type)
    }

    if (!config) {
      console.error(`❌ No campaign configuration found in database for: ${campaign.name} (Key: ${campaignKey})`)
      console.error('   Make sure campaign configurations are loaded via loadCampaignsFromDatabase()')
      // LAST RESORT: Check if we have registry steps but no config (shouldn't happen for configured flows)
      if (registry && campaign.name === 'ACA Health Insurance') {
        return [] // Returning empty will show error, which is correct if we really failed
      }
      return []
    }

    const stepDefinitions = config.steps as (CampaignStageDefinition | ConfigCampaignStep)[]

    if (!stepDefinitions || stepDefinitions.length === 0) {
      console.error(`❌ Campaign configuration has no steps for: ${campaign.name}`)
      return []
    }

    // ✅ Map campaign config steps to actual React components
    const steps: CampaignStepConfig[] = stepDefinitions
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((step, index) => {
        const componentName = step.component
        const Component =
          COMPONENT_REGISTRY[componentName] ||
          registry?.components[componentName]

        if (!Component) {
          console.error(`❌ Component not found: ${componentName}`)
        }

        const title = 'label' in step ? step.label : step.name
        const isRequired =
          'isOptional' in step
            ? !step.isOptional
            : 'required' in step
              ? step.required !== false
              : true

        return {
          id: step.id,
          title: title || step.id,
          description: step.description || '',
          component: Component || (() => React.createElement('div', {}, `Component not found: ${componentName}`)),
          isRequired,
          order: step.order ?? index
        }
      })


    return steps
  }

  /**
   * Get step by ID for a campaign
   */
  static getStepById(campaign: Campaign, stepId: string): CampaignStepConfig | null {
    const steps = this.createStepsForCampaign(campaign, {})
    return steps.find(step => step.id === stepId) || null
  }

  /**
   * Get next step for a campaign
   */
  static getNextStep(campaign: Campaign, currentStepId: string): CampaignStepConfig | null {
    const steps = this.createStepsForCampaign(campaign, {})
    const currentIndex = steps.findIndex(step => step.id === currentStepId)

    if (currentIndex === -1 || currentIndex >= steps.length - 1) {
      return null
    }

    return steps[currentIndex + 1] || null
  }

  /**
   * Get previous step for a campaign
   */
  static getPreviousStep(campaign: Campaign, currentStepId: string): CampaignStepConfig | null {
    const steps = this.createStepsForCampaign(campaign, {})
    const currentIndex = steps.findIndex(step => step.id === currentStepId)

    if (currentIndex <= 0) {
      return null
    }

    return steps[currentIndex - 1] || null
  }

  /**
   * Get all step IDs for a campaign
   */
  static getStepIds(campaign: Campaign): string[] {
    const steps = this.createStepsForCampaign(campaign, {})
    return steps.map(step => step.id)
  }

  /**
   * Get step index by ID
   */
  static getStepIndex(campaign: Campaign, stepId: string): number {
    const steps = this.createStepsForCampaign(campaign, {})
    return steps.findIndex(step => step.id === stepId)
  }

  /**
   * Check if a step exists
   */
  static hasStep(campaign: Campaign, stepId: string): boolean {
    const steps = this.createStepsForCampaign(campaign, {})
    return steps.some(step => step.id === stepId)
  }
}

/**
 * Campaign validation
 */
export class CampaignValidator {
  /**
   * Validate if a campaign can proceed to the next step
   */
  static canProceedToNextStep(
    campaign: Campaign,
    currentStepId: string,
    enrollmentData: any
  ): boolean {
    const currentStep = CampaignFlowFactory.getStepById(campaign, currentStepId)

    if (!currentStep) {
      console.warn(`⚠️ Step not found: ${currentStepId}`)
      return false
    }

    // If step is not required, always allow proceeding
    if (!currentStep.isRequired) {
      return true
    }

    // Use stepValidation.ts for actual validation
    try {
      const campaignKey = getCampaignKey(campaign)

      if (!campaignKey) {
        console.error('❌ Cannot validate: no campaign key')
        return false
      }

      const campaignConfig = getCampaignConfig(campaignKey)

      if (!campaignConfig) {
        // Without a persisted configuration we default to registry-based completion
        return true
      }

      const stepIndex = currentStep.order
      const validation = validateStepByCampaign(campaignKey, stepIndex, enrollmentData)


      return validation.isValid
    } catch (error) {
      console.error('❌ Validation error:', error)
      return false
    }
  }

  /**
   * Validate if a campaign is complete
   */
  static isCampaignComplete(
    campaign: Campaign,
    enrollmentData: any
  ): boolean {
    const steps = CampaignFlowFactory.createStepsForCampaign(campaign, {})
    const requiredSteps = steps.filter(step => step.isRequired)

    const campaignKey = getCampaignKey(campaign)
    const campaignConfig = campaignKey ? getCampaignConfig(campaignKey) : null

    // Check if all required steps are completed
    const allCompleted = requiredSteps.every(step => {
      try {
        if (!campaignKey || !campaignConfig) {
          // Fall back to assuming required steps are complete when config is unavailable
          return true
        }

        const validation = validateStepByCampaign(campaignKey, step.order, enrollmentData)

        return validation.isCompleted
      } catch (error) {
        console.error(`❌ Error validating step ${step.id}:`, error)
        return false
      }
    })


    return allCompleted
  }

  /**
   * Get validation errors for current step
   */
  static getStepValidationErrors(
    campaign: Campaign,
    currentStepId: string,
    enrollmentData: any
  ): string[] {
    const currentStep = CampaignFlowFactory.getStepById(campaign, currentStepId)

    if (!currentStep || !currentStep.isRequired) {
      return []
    }

    try {
      const campaignKey = getCampaignKey(campaign)

      if (!campaignKey) return ['Campaign configuration not found']

      const campaignConfig = getCampaignConfig(campaignKey)

      if (!campaignConfig) {
        return []
      }

      const stepIndex = currentStep.order
      const validation = validateStepByCampaign(campaignKey, stepIndex, enrollmentData)

      return validation.errors || []
    } catch (error) {
      console.error('❌ Error getting validation errors:', error)
      return ['Validation failed']
    }
  }

  /**
   * Get completion percentage for campaign
   */
  static getCompletionPercentage(
    campaign: Campaign,
    enrollmentData: any
  ): number {
    const steps = CampaignFlowFactory.createStepsForCampaign(campaign, {})
    const requiredSteps = steps.filter(step => step.isRequired)
    const campaignKey = getCampaignKey(campaign)
    const campaignConfig = campaignKey ? getCampaignConfig(campaignKey) : null

    if (requiredSteps.length === 0) return 100

    const completedCount = requiredSteps.filter(step => {
      try {
        if (!campaignKey || !campaignConfig) {
          return true
        }

        const validation = validateStepByCampaign(campaignKey, step.order, enrollmentData)
        return validation.isCompleted
      } catch {
        return false
      }
    }).length

    return Math.round((completedCount / requiredSteps.length) * 100)
  }
}